import React, { FC, ReactNode, createContext, useState } from 'react';
import { Notification, NotificationProps } from '@components/molecules/Notification';
import { NOTIFICATIONS_DISMISS_SECONDS } from '@constants';

type NotificationWithId = NotificationProps & { id: string };

interface ShowDefaultNotificationProps {
    message: NotificationProps['message'];
    type: NotificationProps['type'];
    autoDismiss?: NotificationProps['autoDismiss'];
    children?: NotificationProps['children'];
}

interface ShowProductAddedToCartNotificationProps {
    message: NotificationProps['message'];
    product: NotificationProps['product'];
}

type NotificationContext = {
    showDefaultNotification: (props: ShowDefaultNotificationProps) => void;
    showProductAddedToCartNotification: (props: ShowProductAddedToCartNotificationProps) => void;
    clearNotifications: () => void;
};

type NotificationContextProvider = {
    children: ReactNode;
};

const MIN_ID = 1;
const MAX_ID = 99999;

export const NotificationsContext = createContext<NotificationContext>({
    showDefaultNotification: () => {},
    showProductAddedToCartNotification: () => {},
    clearNotifications: () => {},
});

export const NotificationsProvider: FC<NotificationContextProvider> = ({ children }) => {
    const [notifications, setNotifications] = useState<NotificationWithId[]>([]);

    const addNotification = (notification: NotificationWithId) => {
        setNotifications((notifications) => [...notifications, notification]);
    };

    const removeNotification = (id: string) => {
        setNotifications((notifications) => [...notifications.filter((item) => item.id !== id)]);
    };

    const getRandomId = () => `${Math.floor(Math.random() * (MAX_ID - MIN_ID + 1)) + MIN_ID}`;

    const contextValue: NotificationContext = {
        showDefaultNotification: (props) => {
            const notification: NotificationWithId = {
                id: getRandomId(),
                size: 'md',
                position: {
                    top: '0',
                    align: 'center',
                },
                dismissible: true,
                autoDismiss: NOTIFICATIONS_DISMISS_SECONDS,
                borderRadiusStyle: 'rounded',
                children: <></>,
                ...props,
            };
            addNotification(notification);
        },
        showProductAddedToCartNotification: (props) => {
            const notification: NotificationWithId = {
                id: getRandomId(),
                ...props,
                size: 'sm',
                position: {
                    top: '0',
                    align: 'right',
                },
                endIcon: 'fa-light fa-basket-shopping',
                dismissible: false,
                autoDismiss: NOTIFICATIONS_DISMISS_SECONDS,
                borderRadiusStyle: 'not-rounded',
                type: 'success',
                children: <></>,
            };
            addNotification(notification);
        },
        clearNotifications: () => setNotifications([]),
    };

    return (
        <NotificationsContext.Provider value={contextValue}>
            <div>
                <div style={{ position: 'absolute', width: '100%', zIndex: '1056' }}>
                    {notifications.map((notification, index) => {
                        const headerHeight = document.querySelector('header')?.clientHeight || 0;
                        const rightNotificationsOffset = index * 200;
                        const centerNotificationsOffset = index * 130;
                        return (
                            <Notification
                                key={`notification-${notification.id}`}
                                {...notification}
                                position={
                                    notification.position?.align === 'right'
                                        ? {
                                              ...notification.position,
                                              top: `${headerHeight + rightNotificationsOffset}px`,
                                          }
                                        : {
                                              ...notification.position,
                                              top: `${headerHeight + centerNotificationsOffset}px`,
                                          }
                                }
                                onClose={() => removeNotification(notification.id)}
                            />
                        );
                    })}
                </div>
                {children}
            </div>
        </NotificationsContext.Provider>
    );
};
